/**
 * SOPRunnerContext — a global context that owns the currently-running SOP.
 * The runner panel is rendered at the AppShell level so it survives route
 * navigation (a "navigate" step in a SOP can route the user elsewhere while
 * the runner stays on screen).
 */
import { createContext, useContext, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import SOPRunnerPanel from "@/components/sops/SOPRunnerPanel";

const Ctx = createContext({
  runId: null,
  startRun: async () => null,
  openRun: () => {},
  closeRun: () => {},
});

export function useSopRunner() { return useContext(Ctx); }

export function SopRunnerProvider({ children }) {
  const [runId, setRunId] = useState(null);

  const startRun = useCallback(async (sop, extra = {}) => {
    try {
      const payload = {
        sop_id: sop.id || sop,
        ...extra,
      };
      const { data } = await api.post("/sop-runs", payload);
      setRunId(data.id);
      toast.success(`Running "${sop.title || 'SOP'}"`);
      return data.id;
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't start SOP run")); return null; }
  }, []);

  const openRun = useCallback((id) => setRunId(id), []);
  const closeRun = useCallback(() => setRunId(null), []);

  return (
    <Ctx.Provider value={{ runId, startRun, openRun, closeRun }}>
      {children}
      {runId && <SOPRunnerPanel runId={runId} onClose={closeRun} />}
    </Ctx.Provider>
  );
}
